import '@workspace/ui/globals.css';
import '~/styles/common.scss';

import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { ModalStockChanged } from '~/components/ModalStockChanged';
import { ModalStockChangedV3 } from '~/components/ModalStockChangedV3';
import { ModalUpdateRequired } from '~/components/ModalUpdateRequired';
import type { IntercomType } from '~/hooks/useIntercom';
import { StoresProvider } from '~/hooks/useStore';
import type { DeliveryServiceVersion } from '~/stores/DeliveryStore/interfaces';

import type {
  CategoriesResponse,
  CategoryListAPI,
  ProductAPI,
  ProductsPagedResponse,
} from './api/Catalog';
import type { ReviewsResponse } from './api/Customer';
import type { ETACalculation } from './api/ETA';
import { KYCStatus } from './api/KYCStatus';
import { BREAKPOINTS } from './breakpoints';
import Alerts from './components/Alert/Alert';
import CookieBanner from './components/CookieBanner/CookieBanner';
import { ModalType } from './components/Modal/interface';
import { deliveryAddressStore } from './components/ModalDeliveryAddress/DeliveryAddressStore';
import ModalList from './components/ModalList/ModalList';
import NotificationContainer from './components/NotificationContainer/NotificationContainer';
import PromocodeNotifications from './components/PromocodeNotifications/PromocodeNotifications';
import SideBarChooseCompany from './components/SideBarChooseCompany/SideBarChooseCompany';
import GlobalHooks from './hooks/GlobalHooks';
import { GlobalContext } from './hooks/useGlobal';
import { useLang } from './hooks/useLang';
import useMediaQuery from './hooks/useMediaQuery';
import { useModal } from './hooks/useModal';
import { useNavigationAfterVerification } from './hooks/useNavigationAfterVerification';
import { useNavigationTracking } from './hooks/useNavigationTracking';
import useSommelierButtonScript from './hooks/useSommelierButtonScript';
import { useStoreRedirect } from './hooks/useStoreRedirect';
import OnboardingFactory from './pages/OnboardingV3';
import Root from './pages/Root/Root';
import { mainStore, userStore } from './stores';
import type { ISnap } from './stores/interfaces/Midtrans';
import { isVersionGreaterOrEqual } from './utils/platform';

import 'react-loading-skeleton/dist/skeleton.css';

const MIN_SUPPORTED_VERSION = '1.0.41';

declare global {
  interface Window {
    fbq: (track: string, event: string, data?: Record<string, unknown>) => void;
    snap: ISnap;
    __INITIAL_STATE__: {
      context: GlobalContext;
      product?: ProductAPI;
      etaData?: ETACalculation;
      reviews?: ReviewsResponse;
      catalogCategory?: ProductsPagedResponse;
      homeCategories?: CategoriesResponse;
      shopCategories: CategoryListAPI[];
      heroBanners?: CategoriesResponse;
      deliveryVersion: DeliveryServiceVersion;
      /**
       * Set to true when the SSR preload phase failed (5xx). Client-side
       * stores use it to force a refetch on hydration instead of rendering
       * an empty page served from the degraded shell.
       */
      ssrDegraded?: boolean;
    };
    Intercom: IntercomType;
    intercomSettings: {
      app_id: string;
      [key: string]: any;
    };
  }
}

interface AppProps {
  globalContext?: Partial<GlobalContext>;
  serverLang?: string;
}

const App = ({ globalContext = {}, serverLang }: AppProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const { search } = location;
  const { i18n } = useTranslation();
  useLang();
  useNavigationTracking();

  useEffect(() => {
    if (deliveryAddressStore.isOutOfZoneModalOpen) {
      openModal(ModalType.OutOfZone);
    }
  }, [deliveryAddressStore.isOutOfZoneModalOpen]);

  const global: GlobalContext = {
    ...globalContext,
    isSSR: typeof window === 'undefined',
    isMobile: useMediaQuery(
      `(max-width: ${BREAKPOINTS.sm}px)`,
      !!globalContext.isMobile,
    ),
    isTablet: useMediaQuery(
      `(max-width: ${BREAKPOINTS.tablet}px)`,
      !!globalContext.isTablet,
    ),
  };

  if (serverLang) {
    i18n.changeLanguage(serverLang);
  }

  useNavigationAfterVerification();
  useStoreRedirect();
  useSommelierButtonScript({
    isMobile: globalContext?.isMobile,
    isTablet: globalContext?.isTablet,
  });

  useEffect(() => {
    //TODO: remove in 2-3 moths
    mainStore.sendToRN('removeTag', 'warehouse_id');

    if (
      !mainStore.onboardingSeen &&
      mainStore.isRN &&
      company.hasOnboarding &&
      location.pathname !== '/legals'
    ) {
      navigate('/onboarding', { replace: true });
    } else {
      if (!company.newDesignSystemEnabled) {
        mainStore.sendToRN('setStatusBar', 'dark-content');
      }
    }
  }, [
    mainStore.onboardingSeen,
    userStore.personalData.isAdult,
    location.pathname,
  ]);

  useEffect(() => {
    if (!search || import.meta.env.REACT_APP_ENV === 'production') {
      return;
    }

    // window.history.back();
  }, [search]);

  if (!mainStore.isAllStoresSynchronized) {
    return <></>;
  }

  const isIndexablePage =
    location.pathname.startsWith('/c/') || location.pathname.startsWith('/p/');

  return (
    <GlobalContext.Provider value={global}>
      <StoresProvider>
        <GlobalHooks />
        <Alerts />
        <NotificationContainer />
        <CookieBanner />
        <div dir={userStore.dir}>
          <Helmet>
            <title>{company.config.name}</title>
            <meta name="description" content={company.config.name} />
            <meta
              data-react-helmet="true"
              name="robots"
              content={isIndexablePage ? 'index, follow' : 'noindex, nofollow'}
            />
            {company.isEnabledIndexNow && (
              <meta
                name="msvalidate.01"
                content={
                  import.meta.env.INDEXNOW_KEY ||
                  '99c601bc3999477ca8484b82b5e83496'
                }
              />
            )}
            <link
              data-react-helmet="true"
              rel="canonical"
              href={company.config.canonical + (location.pathname ?? '')}
            />
          </Helmet>
          {import.meta.env.REACT_APP_ENV === 'production' ? null : (
            <SideBarChooseCompany />
          )}
          <Routes>
            {company.hasOnboarding && mainStore.isRN && (
              <Route path="/onboarding" element={<OnboardingFactory />} />
            )}
            <Route path="*" element={<Root />} />
          </Routes>
          <ModalList />
          {/* This modal placed here 'cuz it should open independent of the `modal` query param */}
          <ModalStockChangedComponent />
          {/* Blocking modal for outdated app version - only for RN and CityDrinks */}
          {company.isUpdateRequiredModalEnabled &&
            mainStore.isRN &&
            mainStore.appVersion &&
            !isVersionGreaterOrEqual(
              mainStore.appVersion,
              MIN_SUPPORTED_VERSION,
            ) && <ModalUpdateRequired />}
          {userStore.isAuthorized &&
            userStore.personalData.kycStatus === KYCStatus.Verified && (
              <PromocodeNotifications />
            )}
        </div>
      </StoresProvider>
    </GlobalContext.Provider>
  );
};

const ModalStockChangedComponent = company.match({
  [CompanyName.CityDrinks]: company.newDesignSystemEnabled
    ? ModalStockChangedV3
    : ModalStockChanged,
  v3: ModalStockChangedV3,
  default: ModalStockChanged,
});

export default observer(App);
